/**
 * Retour d'OAuth : le serveur peut renvoyer le navigateur avec l'erreur dans l'URL (`?error=…&error_code=…&error_description=…`,
 * ou dans le fragment pour l'ancien format). On la lit UNE fois, on nettoie l'URL, et on la dit en français plutôt que de
 * laisser un formulaire vide.
 *
 * Aucune phrase ne révèle l'existence d'un autre compte : un refus d'inscription (« signup_disabled ») se lit comme un
 * refus d'accès, exactement comme un compte Google inconnu qui passerait le contrôle de la console.
 */
import { MESSAGE_REFUS_GOOGLE } from './acces'

export const MESSAGE_GOOGLE_INACTIF = "La connexion Google n'est pas encore activée."
export const MESSAGE_GOOGLE_ANNULE = 'Connexion Google annulée.'
export const MESSAGE_GOOGLE_SANS_EMAIL = "Google n'a pas transmis d'adresse e-mail : connexion impossible."
export const MESSAGE_GOOGLE_NON_VERIFIE = "Google n'a pas confirmé cette adresse e-mail : connexion impossible. Utilisez une adresse Google vérifiée."
export const MESSAGE_GOOGLE_EXPIRE = 'La connexion Google a expiré. Réessayez.'
export const MESSAGE_GOOGLE_ECHEC = 'Connexion Google impossible. Réessayez ou utilisez votre e-mail.'
export const MESSAGE_GOOGLE_RESEAU = 'Connexion Google impossible pour le moment. Vérifiez votre connexion.'

const PARAMETRES_ERREUR = ['error', 'error_code', 'error_description']

/**
 * L'URL débarrassée des seuls paramètres d'erreur d'OAuth. Le reste de la chaîne de requête (`?apercu`…) et le fragment de
 * route sont conservés : remplacer l'URL par son seul chemin fait perdre l'état de la page.
 */
export function urlSansErreurOAuth(href: string): string {
  const url = new URL(href)
  for (const p of PARAMETRES_ERREUR) url.searchParams.delete(p)
  const dansFragment = new URLSearchParams(url.hash.replace(/^#/, ''))
  const fragmentEstErreur = PARAMETRES_ERREUR.some((p) => dansFragment.has(p))
  return `${url.pathname}${url.search}${fragmentEstErreur ? '' : url.hash}`
}

/** Phrase pour une erreur de retour (code, code détaillé, description du serveur). */
export function phraseErreurRetour(code: string, codeDetail: string, description: string): string {
  const brut = `${code} ${codeDetail} ${description}`.toLowerCase()
  /* Inscription fermée sur ce produit : pour la personne, c'est « pas d'accès », sans plus. */
  if (/signup_disabled|signups not allowed|signup.*disabled/.test(brut)) return MESSAGE_REFUS_GOOGLE
  if (code === 'access_denied') return MESSAGE_GOOGLE_ANNULE
  if (/not enabled|unsupported provider/.test(brut)) return MESSAGE_GOOGLE_INACTIF
  if (/needs_verification|unverified email|not verified/.test(brut)) return MESSAGE_GOOGLE_NON_VERIFIE
  if (/bad_oauth_state|bad_oauth_callback|flow_state|code verifier|invalid flow state|expired/.test(brut)) return MESSAGE_GOOGLE_EXPIRE
  if (/e-?mail/.test(brut)) return MESSAGE_GOOGLE_SANS_EMAIL
  return MESSAGE_GOOGLE_ECHEC
}

/** Lecture PURE d'une adresse : la phrase d'erreur (ou `null`) et l'adresse nettoyée. */
export function lireErreurDansUrl(href: string): { message: string; urlNettoyee: string } | null {
  let url: URL
  try {
    url = new URL(href)
  } catch {
    return null
  }
  const sources = [url.searchParams, new URLSearchParams(url.hash.replace(/^#/, ''))]
  const lire = (cle: string) => sources.map((s) => s.get(cle)).find((v) => v) ?? ''
  const code = lire('error')
  const description = lire('error_description')
  if (!code && !description) return null
  return {
    message: phraseErreurRetour(code, lire('error_code'), description),
    urlNettoyee: urlSansErreurOAuth(url.href),
  }
}

let dejaLu: string | null | undefined

/**
 * Dans le navigateur : lit l'erreur de retour, nettoie la barre d'adresse, rend la phrase (ou `null`).
 * Lue UNE seule fois par chargement de page : un second appel (double montage d'un effet en développement) rend la même
 * phrase au lieu de la perdre, l'adresse étant déjà nettoyée.
 */
export function erreurDeRetourOAuth(): string | null {
  if (dejaLu !== undefined) return dejaLu
  const lu = lireErreurDansUrl(window.location.href)
  if (lu) window.history.replaceState(window.history.state, '', lu.urlNettoyee)
  dejaLu = lu ? lu.message : null
  return dejaLu
}
